import { useState, useCallback, useEffect } from 'react'
import { Upload, File, X, Music, CheckCircle, Trash2 } from 'lucide-react'
import { uploadFiles, type UploadResponse } from '../lib/api'
import { formatBytes, formatDuration } from '../lib/utils'

const STORAGE_KEY = 'stem-separation-uploaded-files'

interface FileUploadProps {
  onUploadComplete: (files: UploadResponse[]) => void
}

export function FileUpload({ onUploadComplete }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<UploadResponse[]>([])
  const [error, setError] = useState<string | null>(null)

  // Load persisted uploads on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as UploadResponse[]
        if (parsed.length > 0) {
          setUploadedFiles(parsed)
          onUploadComplete(parsed)
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [])

  // Persist uploads to localStorage
  useEffect(() => {
    if (uploadedFiles.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(uploadedFiles))
    }
  }, [uploadedFiles])

  const clearUploadedFiles = useCallback(() => {
    setUploadedFiles([])
    localStorage.removeItem(STORAGE_KEY)
    onUploadComplete([])
  }, [onUploadComplete])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase()
      return ['wav', 'mp3', 'flac', 'm4a', 'ogg'].includes(ext || '')
    })
    
    setFiles(prev => [...prev, ...droppedFiles])
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)])
    }
  }, [])

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleUpload = async () => {
    if (files.length === 0) return

    setUploading(true)
    setError(null)

    try {
      const result = await uploadFiles(files)
      setUploadedFiles(result)
      onUploadComplete(result)
      setFiles([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <h2 className="section-title mb-4">
        <Upload className="w-5 h-5 text-primary-400" />
        Upload Audio
      </h2>

      <div
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
          isDragging
            ? 'border-primary-500 bg-primary-500/10'
            : 'border-surface-600/50 hover:border-surface-500/50 hover:bg-surface-800/30'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-surface-800/50 flex items-center justify-center">
          <Music className="w-6 h-6 text-gray-500" />
        </div>
        <p className="text-sm text-gray-300 mb-1">
          Drop audio files here
        </p>
        <p className="text-xs text-gray-500 mb-3">
          WAV, MP3, FLAC, M4A, OGG
        </p>
        <label className="btn-secondary text-sm cursor-pointer inline-flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Browse
          <input
            type="file"
            multiple
            accept=".wav,.mp3,.flac,.m4a,.ogg,.wma,.aac,.aiff"
            className="hidden"
            onChange={handleFileSelect}
          />
        </label>
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-xs text-gray-400 mb-2">
            {files.length} file{files.length !== 1 ? 's' : ''} selected
          </div>
          
          <div className="max-h-32 overflow-y-auto space-y-1.5">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-surface-800/30 rounded-lg px-3 py-2 group"
              >
                <div className="flex items-center gap-2 overflow-hidden min-w-0">
                  <File className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm truncate">{file.name}</span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatBytes(file.size)}
                  </span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-500 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="btn-primary w-full mt-3 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload {files.length} File{files.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} ready
            </div>
            <button
              onClick={clearUploadedFiles}
              className="text-gray-500 hover:text-red-400 p-1 transition-colors"
              title="Clear uploaded files"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-1">
            {uploadedFiles.slice(0, 3).map((file, i) => (
              <p key={i} className="text-xs text-gray-400 truncate">
                {file.filename} ({formatBytes(file.file_size_bytes)})
                {file.duration_sec && ` • ${formatDuration(file.duration_sec)}`}
              </p>
            ))}
            {uploadedFiles.length > 3 && (
              <p className="text-xs text-gray-500">
                +{uploadedFiles.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </div>
  )
}
